import { useEffect, useState } from 'react';
import { FiHeart, FiShoppingCart, FiBell, FiUser } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import { apiGetOrders } from '../../api';
import { getUnreadCount, NOTIFICATION_READ_EVENT } from '../../utils/notifications';
import GlobalSearch from '../search/GlobalSearch';
import CountrySelector from './CountrySelector';
import logo from "../../assets/logo.png";
import headerBackground from '../../assets/HeaderBG.png';
const Navbar = () => {
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const profileDestination = (tab) => `/profile?tab=${tab}`;

  const handleProfileClick = (event) => {
    if (window.innerWidth >= 1024 || location.pathname !== '/profile') return;
    event.preventDefault();
    window.dispatchEvent(new Event('open-profile-menu'));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadNotifs(0);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      apiGetOrders()
        .then((data) => {
          if (cancelled) return;
          const list = data.orders || data || [];
          setUnreadNotifs(getUnreadCount(list));
        })
        .catch(() => { });
    };
    load();
    const timer = setInterval(load, 60000);
    const handleReadChange = (event) => {
      if (Number.isInteger(event.detail?.unreadCount)) {
        setUnreadNotifs(Math.max(0, event.detail.unreadCount));
      } else {
        load();
      }
    };
    window.addEventListener(NOTIFICATION_READ_EVENT, handleReadChange);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(NOTIFICATION_READ_EVENT, handleReadChange);
    };
  }, [isAuthenticated]);

  return (
    <nav
      className="bg-cover bg-white bg-center bg-no-repeat shadow-sm border-b px-4 sticky top-0 z-50"
      style={{ backgroundImage: `url(${headerBackground})` }}
    >
      <div className="relative w-full mx-auto flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src={logo}
            alt="Ekamithila"
            className="logo object-contain"
          />

        </Link>

        {/* Global Search (Desktop) */}
        <div className="absolute left-1/2 top-1/2 hidden w-[min(42vw,36rem)] -translate-x-1/2 -translate-y-1/2 md:block">
          <GlobalSearch />
        </div>

        {/* Right Actions */}
        <ul className="flex items-center gap-2 sm:gap-3 md:gap-4 list-none">
          <li>
            <CountrySelector />
          </li>
          <li>
            {isAuthenticated && user ? (
              <Link
                to="/profile"
                state={{ openProfileMenu: true }}
                onClick={handleProfileClick}
                aria-label="Open account menu"
                className="flex items-center gap-1.5 text-black hover:text-orange-600 transition"
              >
                <span className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-orange-600">
                  {user.name.charAt(0).toUpperCase()}
                </span>
                <span className="text-xs hidden md:block font-medium">
                  {user.name.split(' ')[0]}
                </span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1.5 text-black hover:text-orange-600 transition"
              >
                <span className="w-8 h-8 rounded-full border border-black bg-white/70 flex items-center justify-center">
                  <FiUser className="text-sm text-black" />
                </span>
                <span className="text-xs hidden md:block font-medium">Sign In</span>
              </Link>
            )}
          </li>
          <li>
            <Link
              to={isAuthenticated ? profileDestination('wishlist') : '/login'}
              state={!isAuthenticated ? { from: profileDestination('wishlist') } : undefined}
              className="flex items-center gap-1 text-black hover:text-orange-600 transition relative"
              aria-label={`Bookshelf${wishlistCount > 0 ? ` (${wishlistCount} items)` : ''}`}
            >
              <FiHeart className="text-xl" />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {wishlistCount}
                </span>
              )}
            </Link>
          </li>
          {isAuthenticated && (
            <li>
              <Link
                to={profileDestination('notifications')}
                className="flex items-center gap-1 text-black hover:text-orange-600 transition relative"
                aria-label={`Notifications${unreadNotifs > 0 ? ` (${unreadNotifs} unread)` : ''}`}
              >
                <FiBell className="text-xl" />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full">
                    {unreadNotifs > 9 ? '9+' : unreadNotifs}
                  </span>
                )}
              </Link>
            </li>
          )}
          <li>
            <Link
              to="/cart"
              className="flex items-center gap-1 text-black hover:text-orange-600 transition relative"
              aria-label={`Cart${cartCount > 0 ? ` (${cartCount} items)` : ''}`}
            >
              <FiShoppingCart className="text-xl" />
              <span className="text-xs hidden md:block">Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>
          </li>
        </ul>
      </div>

      {/* Mobile Search (Below navbar) */}
      <div className="mt-2.5 block pb-3 md:hidden">
        <GlobalSearch />
      </div>
    </nav>
  );
};

export default Navbar;
